import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import MemberCard from '../components/MemberCard';
import config from '../config.json';
import { useAuth } from '../AuthContext';

// API URL 설정
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 멤버 페이지 컴포넌트
function Members() {
  const { isAdmin } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // 이미지 URL 처리 헬퍼 함수
  const getImageUrl = (imageLink) => {
    if (!imageLink) return '/images/default.jpg';
    // Base64 이미지인 경우 그대로 반환
    if (imageLink.startsWith('data:image')) return imageLink;
    // 일반 경로인 경우 전체 URL 조합
    return `${config.images.serverUrl}${config.images.basePath}${imageLink}`;
  };

  // 데이터 가져오기
  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/members`);
      const data = await response.json();

      if (data.success) {
        setMembers(data.data);
      } else {
        setError('Failed to load members');
      }
    } catch (err) {
      console.error('Error fetching members:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  // Lab Chief 확인 함수
  const isLabChief = (member) => {
    return member.status === 'chief';
    // return config.labChief.memberIds.includes(member.name);
  };

  // status별로 멤버 그룹화
  const groupedMembers = {
    professor: members.filter(m => m.status === 'professor'),
    chief: members.filter(m => isLabChief(m)), // config.json에서 확인
    phd: members.filter(m => m.status === 'phd' && !isLabChief(m)), // Lab Chief 제외
    integrated: members.filter(m => m.status === 'integrated'),
    master: members.filter(m => m.status === 'master'),
    intern: members.filter(m => m.status === 'intern')
    // alumni는 제외 - 웹 페이지에 표시하지 않음
  };

  // 로딩 중
  if (loading) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <p>Loading members...</p>
      </div>
    );
  }

  // 에러 발생
  if (error) {
    return (
      <div className="site-content" style={{ textAlign: 'center', padding: '50px' }}>
        <p style={{ color: 'red' }}>{error}</p>
        <button onClick={fetchMembers}>Retry</button>
      </div>
    );
  }

  return (
    <div className="site-content">
      {/* Register 버튼 - 관리자에게만 표시 */}
      {isAdmin && (
        <div style={{ textAlign: 'right', margin: '20px 0' }}>
          <Link to="/register-member" style={{
            display: 'inline-block',
            padding: '8px 20px',
            backgroundColor: '#222',
            color: '#fff',
            textDecoration: 'none',
            borderRadius: '3px',
            fontSize: '14px',
            border: 'none'
          }}>
            Register a Member
          </Link>
        </div>
      )}

      {/* Professor 섹션 */}
      {groupedMembers.professor.length > 0 && (
        <>
          <h2 className="section-title">Professor</h2>
          {groupedMembers.professor.map(member => (
            <div key={member.ID} className="professor-section">
              <div className="professor-photo">
                <img 
                  src={getImageUrl(member.image_link)}
                  alt={member.name}
                />
              </div>
              <div className="professor-info">
                <h3><b>{member.name}</b></h3>
                <p><b>E-mail</b>: {member.email}</p>
                {member.tel && <p><b>Tel</b>: {member.tel}</p>}
                <p><b>Research Interest</b>: {member.research_interest}</p>
                {member.info && (
                  <>
                    <br />
                    <p>{member.info}</p>
                  </>
                )}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Lab Chief 섹션 */}
      {groupedMembers.chief.length > 0 && (
        <>
          <h2 className="section-title">Lab Chief</h2>
          <div className="member-grid">
            {groupedMembers.chief.map(member => (
              <MemberCard
                isAdmin={isAdmin}
                memberId={member.ID}
                key={member.ID}
                name={member.name}
                email={member.email}
                photo={`${config.images.serverUrl}${config.images.basePath}${member.image_link}`}
                researchInterest={member.research_interest}
                github={member.github}
              />
            ))}
          </div>
        </>
      )}

      {/* Ph.D Student 섹션 */}
      {groupedMembers.phd.length > 0 && (
        <>
          <h2 className="section-title">Ph.D Student</h2>
          <div className="member-grid">
            {groupedMembers.phd.map(member => (
              <MemberCard
                isAdmin={isAdmin}
                memberId={member.ID}
                key={member.ID}
                name={member.name}
                email={member.email}
                photo={`${config.images.serverUrl}${config.images.basePath}${member.image_link}`}
                researchInterest={member.research_interest}
                github={member.github}
              />
            ))}
          </div>
        </>
      )}

      {/* Integrated M.S./Ph.D. Student 섹션 */}
      {groupedMembers.integrated.length > 0 && (
        <>
          <h2 className="section-title">Integrated M.S./Ph.D. Student</h2>
          <div className="member-grid">
            {groupedMembers.integrated.map(member => (
              <MemberCard
                isAdmin={isAdmin}
                memberId={member.ID}
                key={member.ID}
                name={member.name}
                email={member.email}
                photo={`${config.images.serverUrl}${config.images.basePath}${member.image_link}`}
                researchInterest={member.research_interest}
                github={member.github}
              />
            ))}
          </div>
        </>
      )}

      {/* M.S. Student 섹션 */}
      {groupedMembers.master.length > 0 && (
        <>
          <h2 className="section-title">M.S. Student</h2>
          <div className="member-grid">
            {groupedMembers.master.map(member => (
              <MemberCard
                isAdmin={isAdmin}
                memberId={member.ID}
                key={member.ID}
                name={member.name}
                email={member.email}
                photo={`${config.images.serverUrl}${config.images.basePath}${member.image_link}`}
                researchInterest={member.research_interest}
                github={member.github}
              />
            ))}
          </div>
        </>
      )}

      {/* Intern 섹션 */}
      {groupedMembers.intern.length > 0 && (
        <>
          <h2 className="section-title">Intern</h2>
          <div className="member-grid">
            {groupedMembers.intern.map(member => (
              <MemberCard
                isAdmin={isAdmin}
                memberId={member.ID}
                key={member.ID}
                name={member.name}
                email={member.email}
                photo={`${config.images.serverUrl}${config.images.basePath}${member.image_link}`}
                researchInterest={member.research_interest}
                github={member.github}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default Members;
